import { ReactComponent as DroneSureIcon } from '@assets/components/panel/notification/notification-drone-icon-sure.svg';
import { ReactComponent as DroneUnsureIcon } from '@assets/components/panel/notification/notification-drone-icon-unsure.svg';
import { IcRemoteControl } from '@assets/icons/24x24';
import { PerimeterAlertIcon, SegmentAlertIcon } from '@assets/icons/sprite_icons/sensor';
import { HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { NotificationPlatformList } from '@components/cockpit/panels/notification/card/NotificationPlatformList';
import SensorIcon from '@components/map/popup/infos/sensors/SensorIcon';
import TargetId from '@components/map/popup/infos/targets/TargetId';
import { Tooltip } from '@components/ui/tooltip';
import { css } from '@emotion/react';
import {
  getNotificationColor,
  getNotificationName,
  getWorstNotificationLevel,
  isDroneNotification,
  isSensorNotification,
} from '@utils/data/notification.utils';
import { hexColor, targetColor } from '@utils/map/map.constants';
import { getSensorIconKey } from '@utils/map/sensor.utils';
import { sensorColorLabelFromSensorStatus } from '@utils/sensors/configuration.constants';
import { getUniqueSensorShortHand } from '@utils/target.utils';
import { FormattedMessage, useIntl } from 'react-intl';

import { NotificationType, NotificationTypeEnum, SegmentAlertLevelEnum } from '@/types/data/data.types';
import { ReliabilityEnum } from '@/types/sensor/identification.types';
import { SpecificStateEnum } from '@/types/sensor/sensors';

export function getNotificationIcon(notification: NotificationType, opt?: { isArchived?: boolean; isToast?: boolean }) {
  const { isArchived, isToast } = { isArchived: false, isToast: false, ...opt };
  switch (notification.type) {
    case NotificationTypeEnum.DRONE: {
      const isExpiredStyle = notification.endTime !== null && !isArchived;
      const color = targetColor[notification.classification];
      return (
        <Icon
          as={notification.reliability === ReliabilityEnum.SURE ? DroneSureIcon : DroneUnsureIcon}
          width="48px"
          height="48px"
          css={css({
            '#background': {
              fill: isExpiredStyle ? 'black' : color,
            },
            '#line': {
              fill: isExpiredStyle ? color : 'black',
            },
          })}
        />
      );
    }
    case NotificationTypeEnum.SENSOR: {
      return (
        <SensorIcon
          size="sm"
          sensorIconName={getSensorIconKey(notification.sensorType, true, false, null, notification.sensorStatus, true)}
        />
      );
    }
    case NotificationTypeEnum.SEGMENT: {
      const level = getWorstNotificationLevel(notification, { isArchived, isToast });
      const color = hexColor[sensorColorLabelFromSensorStatus[SpecificStateEnum.EFFRACTION]];
      if (level === SegmentAlertLevelEnum.PERIMETER_EFFRACTION) {
        return <Icon as={PerimeterAlertIcon} width="48px" height="48px" color={color} />;
      } else {
        return <Icon as={SegmentAlertIcon} width="48px" height="48px" color={color} />;
      }
    }
  }
}

type Props = {
  notification: NotificationType;
  isArchived: boolean;
  isSearch: boolean;
  isSelected?: boolean;
};

function NotificationCardName({ notification, isArchived, isSearch, isSelected = false }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const level = getWorstNotificationLevel(notification, { isArchived });
  const color = getNotificationColor(notification, { isArchived });
  const showCurrent = !isArchived && notification.endTime == null;

  const notificationName = getNotificationName(notification);
  const isDroneNotif = isDroneNotification(notification);
  const showRemoteControl =
    isDroneNotif && (showCurrent ? notification.withRemoteControl : notification.hasDetectedRemoteControl);

  function getWidth() {
    if (isSearch) {
      return '268px';
    } else if (isArchived) {
      return '296px';
    }
    return '340px';
  }

  return (
    <HStack gap={2} paddingLeft={3} paddingRight={2} width={getWidth()} position="relative">
      {getNotificationIcon(notification, { isArchived })}
      <VStack gap={1} alignItems="start" width="100%">
        <Text backgroundColor="neutral.black" paddingX={0.5} color={color} lineHeight="18px">
          <FormattedMessage
            id={`notification.name.${level}`}
            values={{
              value: isSensorNotification(notification)
                ? formatMessage({ id: `sensors.type.${notification.sensorType}` })
                : undefined,
            }}
          />
        </Text>
        {
          <Tooltip content={notificationName ?? notification.identifier}>
            {notificationName ? (
              <Text
                paddingX={0.5}
                lineClamp={1}
                maxWidth="100%"
                backgroundColor="neutral.black"
                textTransform="uppercase"
                lineHeight="18px"
                wordBreak="break-all"
              >
                {notificationName}
              </Text>
            ) : (
              <TargetId label={notification.identifier} isTechnical textStyle="sm" />
            )}
          </Tooltip>
        }
        <NotificationPlatformList marks={notification.marks} isNotificationSelected={isSelected} />
        {isDroneNotif && (
          <HStack width="100%" gap={1}>
            {getUniqueSensorShortHand(notification.trackIds).map((sensor) => (
              <Text key={sensor} color="neutral.300">
                {sensor}
              </Text>
            ))}
          </HStack>
        )}
      </VStack>
      {showRemoteControl && (
        <Icon as={IcRemoteControl} position="absolute" right={1.5} top={-1.5} color="neutral.500" boxSize="24px" />
      )}
    </HStack>
  );
}

export default NotificationCardName;
